import type { CollectionFieldVectorFieldValue } from '@ikegps/core-api';
import { override, makeObservable } from 'mobx';
import { T } from '../../i18n';
import { BearingInputState } from './bearing.input.state';
import { DistanceInputState } from './distance.input.state';
import { InputState } from './input.state';

export class VectorInputState extends InputState<CollectionFieldVectorFieldValue> {
	distance: DistanceInputState;
	bearing: BearingInputState;
	pitch: number | null = null;

	@override override get value(): CollectionFieldVectorFieldValue | null {
		if (this.distance.value == null && this.bearing.value == null) {
			return null;
		}

		return {
			bearing: this.bearing.value,
			distance: this.distance.value,
			pitch: this.pitch,
		};
	}

	static equal(value1: CollectionFieldVectorFieldValue, value2: CollectionFieldVectorFieldValue) {
		if (value1 === value2) {
			return true;
		}

		if (value1 == null || value2 == null) {
			return false;
		}

		return (
			DistanceInputState.equal(value1.distance, value2.distance) &&
			BearingInputState.equal(value1.bearing, value2.bearing) &&
			value1.pitch === value2.pitch
		);
	}

	constructor(label: string) {
		super(label);

		makeObservable(this);

		this.isLabelShown = false;

		this.distance = new DistanceInputState('Distance');
		this.bearing = new BearingInputState('Bearing');
	}

	@override override setValue(val: CollectionFieldVectorFieldValue) {
		if (val == null) {
			this.distance.setValue(null);
			this.bearing.setValue(null);
			this.pitch = null;
			this.setError(null);
			return;
		}

		this.distance.setValue(val.distance);
		this.bearing.setValue(val.bearing);
		this.pitch = val.pitch;

		if (val.distance == null && val.bearing == null) {
			this.setError(null);
		} else if (val.distance == null) {
			this.setError(T.DISTANCE_MISSING);
		} else if (val.bearing == null) {
			this.setError(T.BEARING_MISSING);
		} else {
			this.setError(null);
		}
	}

	override hasError() {
		return super.hasError() || this.distance.hasError() || this.bearing.hasError();
	}

	@override override get error(): string {
		// Don't show an error message if one is already being shown
		if (this.distance.hasError() || this.bearing.hasError()) {
			return null;
		}

		if (this.distance.value != null && this.bearing.value == null) {
			return T.BEARING_MISSING;
		}

		if (this.distance.value == null && this.bearing.value != null) {
			return T.DISTANCE_MISSING;
		}

		return this._error;
	}

	override set error(msg: string) {
		this._error = msg || '';
	}

	override destroy() {
		super.destroy();
		this.distance.destroy();
	}
}
